import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { appQueryKeys } from "@/app/data/appQueryKeys";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useDeepCompareMemo } from "@/common/hooks/useDeepCompare";
import api from "@/common/utils/api";
import dayjs from "@/common/utils/dayjs";
import { DebugEvent, DebugEventsQueryType } from "@reflag/shared/eventAPI";

const MAX_EVENTS_COUNT = 50;

export function useDebugEventsData(
  params: Omit<DebugEventsQueryType, "envId" | "lastTS"> = {},
  options: {
    live?: boolean;
  } = {},
) {
  const { appId, envId } = useEnvironment();
  const queryClient = useQueryClient();
  const { live } = options;
  const historyHours = params.hasProblem === "true" ? 24 : 72;
  const latestTimestamp = useRef(
    String(dayjs().subtract(historyHours, "hours").valueOf()),
  );

  // Use deep compare memo to avoid unnecessary re-renders due to object reference changes
  const queryParams = useDeepCompareMemo(
    () => ({
      envId,
      ...params,
    }),
    [envId, params],
  );

  // Reset the latest timestamp when the query params change
  useEffect(() => {
    latestTimestamp.current = String(
      dayjs().subtract(historyHours, "hours").valueOf(),
    );
  }, [queryParams, historyHours]);

  return useQuery({
    queryKey: appQueryKeys.debugEvents(appId, envId, queryParams),
    queryFn: async () => {
      const cutoff = dayjs().subtract(historyHours, "hours").valueOf();
      const response = await api.get<"/apps/:appId/debug-events/latest">(
        `/apps/${appId}/debug-events/latest`,
        {
          params: {
            ...queryParams,
            lastTS: String(Math.max(Number(latestTimestamp.current), cutoff)),
          },
        },
      );
      // Get the events that are already stored in the query cache
      const storedEvents =
        queryClient.getQueryData<DebugEvent[]>(
          appQueryKeys.debugEvents(appId, envId, queryParams),
        ) ?? [];
      // Combine the new events with the stored events
      const events = [...response.data.events, ...storedEvents]
        .filter((event) => new Date(event.timestamp).valueOf() > cutoff)
        .slice(0, MAX_EVENTS_COUNT);
      // Update the lastTS to the timestamp of the latest event
      if (events.length > 0) {
        latestTimestamp.current = String(
          new Date(events[0].timestamp).valueOf(),
        );
      }
      return events;
    },
    gcTime: 0,
    refetchInterval: live ? 1000 : false,
  });
}
